import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
  },
  appBarScrolled: {
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 3),
    minHeight: 70,
  },
  logo: {
    height: 50,
    cursor: "pointer",
    objectFit: "contain",
  },
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  navLink: {
    color: theme.palette.text.primary,
    textDecoration: "none",
    fontSize: "1rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "color 0.2s ease",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  ctaButton: {
    marginLeft: theme.spacing(2),
    textTransform: "none",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    borderRadius: 8,
  },
  ctaPrimary: {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  ctaSecondary: {
    backgroundColor: "transparent",
    color: theme.palette.primary.main,
    border: `2px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
    },
  },
  menuButton: {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "block",
    },
  },
  drawer: {
    width: 280,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  drawerList: {
    padding: theme.spacing(2, 0),
  },
  drawerListItem: {
    padding: theme.spacing(2, 3),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  drawerCta: {
    margin: theme.spacing(2, 3),
  },
}));

const NavigationMenu = ({ logo, links = [], ctaButton, sticky = true }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!sticky) return;

    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled, sticky]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLinkClick = (url) => {
    // Handle smooth scroll for anchor links
    if (url.startsWith("#")) {
      const element = document.querySelector(url);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else if (url.startsWith("/")) {
      // Internal navigation
      window.location.href = url;
    } else {
      // External link
      window.open(url, "_blank", "noopener,noreferrer");
    }
    setMobileOpen(false);
  };

  const handleLogoClick = () => {
    if (logo.linkUrl) {
      window.location.href = logo.linkUrl;
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const renderCTAButton = (mobile = false) => {
    if (!ctaButton) return null;

    const buttonClass = ctaButton.style === "secondary" ? classes.ctaSecondary : classes.ctaPrimary;

    return (
      <Button
        variant="contained"
        className={`${classes.ctaButton} ${buttonClass} ${mobile ? classes.drawerCta : ""}`}
        onClick={() => handleLinkClick(ctaButton.url)}
        fullWidth={mobile}
      >
        {ctaButton.text}
      </Button>
    );
  };

  const drawer = (
    <div className={classes.drawer}>
      <div className={classes.drawerHeader}>
        <img
          src={logo.imageUrl}
          alt={logo.altText}
          className={classes.logo}
          onClick={handleLogoClick}
        />
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </div>
      <List className={classes.drawerList}>
        {links.map((link, index) => (
          <ListItem
            button
            key={index}
            className={classes.drawerListItem}
            onClick={() => handleLinkClick(link.url)}
          >
            <ListItemText primary={link.text} />
          </ListItem>
        ))}
      </List>
      {renderCTAButton(true)}
    </div>
  );

  return (
    <>
      <AppBar
        position={sticky ? "sticky" : "static"}
        className={`${classes.appBar} ${scrolled ? classes.appBarScrolled : ""}`}
        elevation={0}
      >
        <Toolbar className={classes.toolbar}>
          <img
            src={logo.imageUrl}
            alt={logo.altText}
            className={classes.logo}
            onClick={handleLogoClick}
          />

          <div className={classes.navLinks}>
            {links.map((link, index) => (
              <a
                key={index}
                className={classes.navLink}
                onClick={() => handleLinkClick(link.url)}
              >
                {link.text}
              </a>
            ))}
            {renderCTAButton()}
          </div>

          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <MenuIcon style={{ color: theme.palette.text.primary }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default NavigationMenu;
